<template>
  <div>
    <div v-if="places.length > 0">
      <v-data-table
        class="businesses-guide-table"
        :headers="headers"
        :header-props="{sortByText: $t('Sort by')}"
        :items="places"
        :items-per-page="50"
        hide-default-footer
        fixed-header
        :disable-pagination="true"
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <a
              :href="getSingleBusinessUrl(item.id)"
              target="_blank"
            >
              <v-img
                v-if="item.preview_image"
                :src="item.preview_image"
                :lazy-src="item.preview_image"
                max-height="70"
                max-width="100"
                class="rounded"
              ></v-img>
            </a>
            <div class="pl-2 mr-2">
              <a
                :href="getSingleBusinessUrl(item.id)"
                target="_blank"
              >
                {{ item.name }}
              </a>
            </div>
          </div>
        </template>

        <template v-slot:item.subscription="{ item }">
          <router-link
            v-if="item.subscription"
            :to="{
              name: 'my-subscriptions.subscription-single',
              params: { id: item.subscription.id, returnTo: { name: 'Businesses' } }
            }"
          >
            <v-chip small class="cursor-pointer hover-underline">
              {{ getPlanName(item.subscription) }}
            </v-chip>
          </router-link>
        </template>

        <template v-slot:item.primary_type.name="{ item }">
          <span v-if="item.primary_type">
            {{ item.primary_type.name[$i18n.locale] || item.primary_type.name['en'] }}
          </span>
        </template>

        <template v-slot:item.city.name="{ item }">
          <span v-if="item.city">
            {{ item.city.name[$i18n.locale] || item.city.name['en'] }}
          </span>
        </template>

        <template v-slot:item.country.name="{ item }">
          <span v-if="item.country">
            {{ item.country.name[$i18n.locale] || item.country.name['en'] }}
          </span>
        </template>

        <template v-slot:item.priceLevel="{ item }">
          {{ item.price_level_display_text }}
        </template>

        <template v-slot:item.active="{ item }">
          <v-icon
            :color="Boolean(item.active) ? 'green' : 'grey lighten-1'"
            class="mr-1"
          >
            mdi-check-circle
          </v-icon>
        </template>

        <template v-slot:item.options="{ item, index }">
          <div class="d-flex align-center">
          <v-menu
              v-model="openActionsMenus[item.id]"
              @input="setActionsMenuValue(item.id, $event)"
              bottom
              left
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  class="business-guide-actions-activator mr-2"
                  :class="{ 'business-guide-actions-activator-first': index === 0 }"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <place-google-sync-action
                  :place-id="item.id"
                  variant="menu-item"
                  :menu-item-class="[
                    'business-guide-sync-action',
                    { 'business-guide-sync-action-first': index === 0 }
                  ]"
                  @synced="load"
                />
                <v-list-item
                  @click="showDeleteModal = true; deletePlaceId = item.id"
                  class="business-guide-delete-action"
                  :class="{ 'business-guide-delete-action-first': index === 0 }"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon color="error">mdi-delete-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t('Delete') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :to="{ name: 'BusinessForm', params: { id: String(item.id) } }"
                  x-small
                  fab
                  color="secondary"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2 business-guide-edit-listing-btn"
                  :class="{ 'business-guide-edit-listing-btn-first': index === 0 }"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Edit') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :href="getSingleBusinessUrl(item.id)"
                  target="_blank"
                  :disabled="!item.active"
                  x-small
                  fab
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-3 business-guide-view-listing-btn"
                  :class="{ 'business-guide-view-listing-btn-first': index === 0 }"
                >
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('View') }}</span>
            </v-tooltip>
            <v-btn
              class="text-decoration-underline text-capitalize cursor-pointer business-guide-details-btn"
              :class="{ 'business-guide-details-btn-first': index === 0 }"
              @click.stop="preview(item)"
            >
              {{ $t('Details') }}
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <place-delete-modal
        :show="showDeleteModal"
        :place-id="deletePlaceId"
        @close="showDeleteModal = false; deletePlaceId = null"
        @deleted="load"
      />

      <place-preview-modal />

    </div>
    <div v-else-if="!isLoading" class="pa-4 text-center">
      {{ $t('businesses.empty-state') }}
    </div>
  </div>
</template>

<script>
import PlaceDeleteModal from '@/components/PlaceDeleteModal.vue';
import PlacePreviewModal from '@/components/PlacePreviewModal.vue';
import PlaceGoogleSyncAction from '@/components/PlaceGoogleSyncAction.vue';
import SubscriptionsMixin from '@/lib/SubscriptionsMixin';

export default {
  mixins: [SubscriptionsMixin],
  components: {
    PlaceDeleteModal,
    PlacePreviewModal,
    PlaceGoogleSyncAction,
  },
  props: {
    limit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      places: [],
      isLoading: true,
      showDeleteModal: false,
      deletePlaceId: null,
      openActionsMenus: {},
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('Name'),
          align: 'start',
          sortable: true,
          value: 'name',
        },
        { text: this.$t('Subscription'), value: 'subscription' },
        { text: this.$t('Type'), value: 'primary_type.name' },
        { text: this.$t('City'), value: 'city.name' },
        { text: this.$t('Country'), value: 'country.name' },
        { text: this.$t('Rating'), value: 'rating' },
        { text: this.$t('User Rating Count'), value: 'userRatingCount' },
        { text: this.$t('Price'), value: 'priceLevel' },
        { text: this.$t('AdFormStatus.active'), value: 'active' },
        { text: this.$t('Options'), value: 'options' },
      ];
    },
  },
  watch: {
    limit() {
      this.load();
    },
  },
  mounted() {
    window.addEventListener('onboarding:business-guide-open-actions-menu', this.openGuideActionsMenuForFirstRow);
    window.addEventListener('onboarding:business-guide-close-actions-menu', this.closeGuideActionsMenuForFirstRow);
    this.load();
  },
  beforeDestroy() {
    window.removeEventListener('onboarding:business-guide-open-actions-menu', this.openGuideActionsMenuForFirstRow);
    window.removeEventListener('onboarding:business-guide-close-actions-menu', this.closeGuideActionsMenuForFirstRow);
  },
  methods: {
    load() {
      this.isLoading = true;
      this.$store.dispatch('loading', true);
      this.$store.dispatch('getPlaces', {
        limit: this.limit || null,
      })
        .then((places) => {
          this.places = places;
          this.isLoading = false;
          this.$store.dispatch('loading', false);
          this.$nextTick(() => {
            if (this.$route.name === 'Businesses') {
              this.$store.dispatch('startBusinessBillingGuideFlow', {
                routeName: this.$route.name,
                router: this.$router,
              });
            }
          });
        })
        .catch(() => {
          this.isLoading = false;
          this.$store.dispatch('loading', false);
        });
    },
    preview(item) {
      this.$store.dispatch('setPlacePreview', item.id);
    },
    setActionsMenuValue(id, value) {
      this.$set(this.openActionsMenus, id, value);
    },
    openGuideActionsMenuForFirstRow() {
      if (!this.places.length) {
        return;
      }
      this.setActionsMenuValue(this.places[0].id, true);
    },
    closeGuideActionsMenuForFirstRow() {
      if (!this.places.length) {
        return;
      }
      this.setActionsMenuValue(this.places[0].id, false);
    },
  },
};
</script>
